<template>
  <div v-if="eventType.reference_number" class="details-wrapper">
    <div class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <ProjectEventTypeDetailsForm
              :event-type="updatedEventType"
              @update="updateOnScreenValue($event.type, $event.value)"
            />
          </section>
          <section
            v-if="canViewAttachments && updatedEventType && updatedEventType.reference_number"
            class="details-section"
          >
            <tol-button
              button-type="major"
              :label="toggleAttachments"
              :icon-name="iconFor(isListDisplayed['ATTACHMENTS'])"
              @click="toggleList('ATTACHMENTS')"
            />
            <div v-if="isListDisplayed['ATTACHMENTS']" class="details-list">
              <h1>{{ $t('projects.eventTypes.details.attachments.title') }}</h1>
              <Attachment
                :reference-number="updatedEventType.reference_number"
                owner="PROJECT_EVENT_TYPE"
                @attachment-success="refreshAttachments"
              />
              <AttachmentsList
                attachment-type="PROJECT_EVENT_TYPE"
                :attachments="eventTypeAttachments"
                :attachment-options="attachmentOptions"
                @delete-attachment="confirmAttachmentDelete"
                @refresh="refreshAttachments"
              />
            </div>
          </section>
          <section class="details-section">
            <tol-button
              button-type="major"
              :label="toggleTechRates"
              :icon-name="iconFor(isListDisplayed['TECH_RATES'])"
              @click="toggleList('TECH_RATES')"
            />
            <div v-if="isListDisplayed['TECH_RATES']" class="details-list-container">
              <div v-if="updatedEventType.technician_rates.length > 0" class="details-list">
                <h1>{{ $t('projects.eventTypes.details.techRates.title') }}</h1>
                <tol-table
                  :table-data="updatedEventType.technician_rates"
                  :columns="techRatesColumns"
                  data-label-prefix="techRates"
                >
                  <template v-slot:tableHeader>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'rate_code'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="rate_code"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.technicianLevel') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'pay_staff'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="pay_staff"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.payStaff') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'pay_sole'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="pay_sole"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.paySole') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'pay_inc'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="pay_inc"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.payInc') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'bill_rate'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="bill_rate"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.billRate') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'start_date'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="start_date"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.startDate') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForTechRates === 'end_date'
                          ? currentSortDirectionForTechRates
                          : 'none'
                      "
                      field-name="end_date"
                      @change="onTechRatesSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.techRates.endDate') }}
                    </th-sortable>
                    <th>{{ $t('projects.eventTypes.details.techRates.comments') }}</th>
                  </template>
                </tol-table>
              </div>
              <div v-if="updatedEventType.technician_rates.length === 0">
                <div class="empty">
                  {{
                    $t('projects.eventTypes.details.errors.noRecords', {
                      record: 'Technician Rates'
                    })
                  }}
                </div>
              </div>
              <div class="add-button">
                <span @click="displayAddModal('TECH_RATES')">+</span>
              </div>
            </div>
          </section>
          <section class="details-section">
            <tol-button
              button-type="major"
              :label="toggleBilling"
              :icon-name="iconFor(isListDisplayed['BILLING'])"
              @click="toggleList('BILLING')"
            />
            <div v-if="isListDisplayed['BILLING']" class="details-list-container">
              <div
                v-if="updatedEventType.labour_rates || updatedEventType.travel_rates"
                class="details-list"
              >
                <h1>{{ $t('projects.eventTypes.details.billing.title') }}</h1>
                <div v-if="updatedEventType.labour_rates" class="billing-container">
                  <div class="billing-dropdown-container">
                    <label>
                      {{ $t('projects.eventTypes.details.billing.activeLabourBillingType') }}
                    </label>
                    <DropdownSelect
                      :menu-items="labourRatesTypes"
                      :selected-item="updatedEventType.labour_rates.active_billing_type"
                      class="bordered billing-dropdown"
                      @select-item="updateOnScreenValue('active_labour_billing_type', $event)"
                    />
                  </div>
                  <div
                    v-if="updatedEventType.labour_rates.active_billing_type === 'ACTUAL'"
                    class="billing-values"
                  >
                    <div class="billing-values-column">
                      <label>
                        {{ $t('projects.eventTypes.details.billing.isPay3HoursMinimumEnabled') }}
                      </label>
                      <Checkbox
                        size="tiny"
                        :checked="updatedEventType.labour_rates.is_pay_3_hours_minimum_enabled"
                        @change="
                          updatedEventType.labour_rates.is_pay_3_hours_minimum_enabled = !updatedEventType
                            .labour_rates.is_pay_3_hours_minimum_enabled
                        "
                      />
                    </div>
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.isBill3HoursMinimumEnabled') }}
                      </label>
                      <Checkbox
                        size="tiny"
                        :checked="updatedEventType.labour_rates.is_bill_3_hours_minimum_enabled"
                        @change="
                          updatedEventType.labour_rates.is_bill_3_hours_minimum_enabled = !updatedEventType
                            .labour_rates.is_bill_3_hours_minimum_enabled
                        "
                      />
                    </div>
                  </div>
                  <div
                    v-if="updatedEventType.labour_rates.active_billing_type === 'FIXED_PER_TECH'"
                    class="billing-values"
                  >
                    <div class="billing-values-column">
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedPayPerTechnicianAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.labour_rates.fixed_pay_per_technician_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_pay_per_technician_amount'] }"
                        @input="updateOnScreenValue('fixed_pay_per_technician_amount', $event)"
                      />
                    </div>
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedBillPerTechnicianAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.labour_rates.fixed_bill_per_technician_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_labour_bill_per_technician_amount'] }"
                        @input="
                          updateOnScreenValue('fixed_labour_bill_per_technician_amount', $event)
                        "
                      />
                    </div>
                  </div>
                  <div
                    v-if="updatedEventType.labour_rates.active_billing_type === 'FIXED_PER_EVENT'"
                    class="billing-values"
                  >
                    <div class="billing-values-column">
                      <label>
                        {{
                          $t(
                            'projects.eventTypes.details.billing.isFixedPayPerEvent3HoursMinimumEnabled'
                          )
                        }}
                      </label>
                      <Checkbox
                        size="tiny"
                        :checked="
                          updatedEventType.labour_rates
                            .is_fixed_pay_per_event_3_hours_minimum_enabled
                        "
                        @change="
                          updatedEventType.labour_rates.is_fixed_pay_per_event_3_hours_minimum_enabled = !updatedEventType
                            .labour_rates.is_fixed_pay_per_event_3_hours_minimum_enabled
                        "
                      />
                    </div>
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedBillPerEventAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.labour_rates.fixed_bill_per_event_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_labour_bill_per_event_amount'] }"
                        @input="updateOnScreenValue('fixed_labour_bill_per_event_amount', $event)"
                      />
                    </div>
                  </div>
                  <div
                    v-if="updatedEventType.labour_rates.active_billing_type === 'FIXED_PER_ASSET'"
                    class="billing-values"
                  >
                    <div class="billing-values-column">
                      <label>
                        {{
                          $t(
                            'projects.eventTypes.details.billing.isFixedPayPerAsset3HoursMinimumEnabled'
                          )
                        }}
                      </label>
                      <Checkbox
                        size="tiny"
                        :checked="
                          updatedEventType.labour_rates
                            .is_fixed_pay_per_asset_3_hours_minimum_enabled
                        "
                        @change="
                          updatedEventType.labour_rates.is_fixed_pay_per_asset_3_hours_minimum_enabled = !updatedEventType
                            .labour_rates.is_fixed_pay_per_asset_3_hours_minimum_enabled
                        "
                      />
                    </div>
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedBillPerAssetAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.labour_rates.fixed_bill_per_asset_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_bill_per_asset_amount'] }"
                        @input="updateOnScreenValue('fixed_bill_per_asset_amount', $event)"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="updatedEventType.travel_rates" class="billing-container">
                  <div class="billing-dropdown-container">
                    <label>
                      {{ $t('projects.eventTypes.details.billing.activeTravelBillingType') }}
                    </label>
                    <DropdownSelect
                      :menu-items="travelRatesTypes"
                      :selected-item="updatedEventType.travel_rates.active_billing_type"
                      class="bordered billing-dropdown"
                      @select-item="updateOnScreenValue('active_travel_billing_type', $event)"
                    />
                  </div>
                  <div
                    v-if="updatedEventType.travel_rates.active_billing_type === 'FIXED_PER_TECH'"
                    class="billing-values"
                  >
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedBillPerTechnicianAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.travel_rates.fixed_bill_per_technician_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_travel_bill_per_technician_amount'] }"
                        @input="
                          updateOnScreenValue('fixed_travel_bill_per_technician_amount', $event)
                        "
                      />
                    </div>
                  </div>
                  <div
                    v-if="updatedEventType.travel_rates.active_billing_type === 'FIXED_PER_EVENT'"
                    class="billing-values"
                  >
                    <div>
                      <label>
                        {{ $t('projects.eventTypes.details.billing.fixedBillPerEventAmount') }}
                      </label>
                      <InputField
                        v-model="updatedEventType.travel_rates.fixed_bill_per_event_amount"
                        input-type="number"
                        class="bordered"
                        :class="{ error: formErrors['fixed_travel_bill_per_event_amount'] }"
                        @input="updateOnScreenValue('fixed_travel_bill_per_event_amount', $event)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!updatedEventType.labour_rates && !updatedEventType.travel_rates">
                <div class="empty">
                  {{
                    $t('projects.eventTypes.details.errors.noRecords', { record: 'Billing rates' })
                  }}
                </div>
              </div>
              <div class="add-button">
                <span @click="displayAddModal('BILLING')">+</span>
              </div>
            </div>
          </section>
          <section class="details-section">
            <tol-button
              button-type="major"
              :label="toggleTools"
              :icon-name="iconFor(isListDisplayed['TOOLS'])"
              @click="toggleList('TOOLS')"
            />
            <div v-if="isListDisplayed['TOOLS']" class="details-list-container">
              <div v-if="updatedEventType.tools.length > 0" class="details-list">
                <h1>{{ $t('projects.eventTypes.details.tools.title') }}</h1>
                <tol-table
                  :table-data="updatedEventType.tools"
                  :columns="toolsColumns"
                  data-label-prefix="tools"
                >
                  <template v-slot:tableHeader>
                    <th-sortable
                      :direction="
                        currentSortFieldForTools === 'name' ? currentSortDirectionForTools : 'none'
                      "
                      field-name="name"
                      @change="onToolsSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.tools.name') }}
                    </th-sortable>
                    <th>{{ $t('projects.eventTypes.details.tools.comments') }}</th>
                  </template>
                </tol-table>
              </div>
              <div v-if="updatedEventType.tools.length === 0">
                <div class="empty">
                  {{ $t('projects.eventTypes.details.errors.noRecords', { record: 'Tools' }) }}
                </div>
              </div>
              <div class="add-button">
                <span @click="displayAddModal('TOOLS')">+</span>
              </div>
            </div>
          </section>
          <section class="details-section">
            <tol-button
              button-type="major"
              :label="toggleSpecialRequirements"
              :icon-name="iconFor(isListDisplayed['SPECIAL_REQUIREMENTS'])"
              @click="toggleList('SPECIAL_REQUIREMENTS')"
            />
            <div v-if="isListDisplayed['SPECIAL_REQUIREMENTS']" class="details-list-container">
              <div v-if="updatedEventType.special_requirements.length > 0" class="details-list">
                <h1>{{ $t('projects.eventTypes.details.specialRequirements.title') }}</h1>
                <tol-table
                  :table-data="updatedEventType.special_requirements"
                  :columns="specialRequirementsColumns"
                  parent-property="requirement"
                  data-label-prefix="specialRequirements"
                >
                  <template v-slot:tableHeader>
                    <th-sortable
                      :direction="
                        currentSortFieldForSpecialRequirements === 'description'
                          ? currentSortDirectionForSpecialRequirements
                          : 'none'
                      "
                      field-name="description"
                      @change="onSpecialRequirementsSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.specialRequirements.description') }}
                    </th-sortable>
                    <th-sortable
                      :direction="
                        currentSortFieldForSpecialRequirements === 'value'
                          ? currentSortDirectionForSpecialRequirements
                          : 'none'
                      "
                      field-name="value"
                      @change="onSpecialRequirementsSortOrderChange"
                    >
                      {{ $t('projects.eventTypes.details.specialRequirements.value') }}
                    </th-sortable>
                    <th>
                      {{ $t('projects.eventTypes.details.specialRequirements.comments') }}
                    </th>
                  </template>
                </tol-table>
              </div>
              <div v-if="updatedEventType.special_requirements.length === 0">
                <div class="empty">
                  {{
                    $t('projects.eventTypes.details.errors.noRecords', {
                      record: 'Special Requirements'
                    })
                  }}
                </div>
              </div>
              <div class="add-button">
                <span @click="displayAddModal('SPECIAL_REQUIREMENTS')">+</span>
              </div>
            </div>
          </section>
          <div class="module-action-container">
            <TolButton
              v-if="canUserSave"
              button-type="save"
              :label="$t('projects.eventTypes.details.labels.save')"
              :is-loading="isSaving"
              :class="[
                'module-action',
                { disabled: !isOnScreenDataModified && updatedEventType.reference_number }
              ]"
              @click="saveEventTypeUpdates"
            />
          </div>
        </div>
      </div>
      <div class="event-type-status" :class="statusClass">{{ updatedEventType.status }}</div>
    </div>
    <update-modal v-if="isLoading">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
    <AddTechRatesModal
      v-if="isModalDisplayed['TECH_RATES']"
      @close="isModalDisplayed['TECH_RATES'] = false"
      @save="addReferencedObject($event)"
    />
    <AddBillingModal
      v-if="isModalDisplayed['BILLING']"
      @close="isModalDisplayed['BILLING'] = false"
      @save="addReferencedObject($event)"
    />
    <AddToolModal
      v-if="isModalDisplayed['TOOLS']"
      :tools="allTools"
      @close="isModalDisplayed['TOOLS'] = false"
      @save="addReferencedObject($event)"
    />
    <AddSpecialRequirementModal
      v-if="isModalDisplayed['SPECIAL_REQUIREMENTS']"
      @close="isModalDisplayed['SPECIAL_REQUIREMENTS'] = false"
      @save="addReferencedObject($event)"
    />
  </div>
</template>

<script>
import AddBillingModal from './components/AddBillingModal.vue';
import AddSpecialRequirementModal from './components/AddSpecialRequirementModal.vue';
import AddTechRatesModal from './components/AddTechRatesModal.vue';
import AddToolModal from './components/AddToolModal.vue';
import { AppActions } from '@/app/app.keys';
import Attachment from '@/app/components/attachments/Attachment';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import Checkbox from '@/app/components/Checkbox';
import { clone } from 'ramda';
import ConfirmModal from '@/app/components/ConfirmModal';
import DropdownSelect from '@/app/components/DropdownSelect';
import { dateUtils } from '@/mixins/date-utils';
import formatDateTime from '@/filters/format-datetime.filter.js';
import InputField from '@/app/components/InputField';
import {
  ProjectEventTypeDetailsActions,
  ProjectEventTypeDetailsGetters
} from './project-event-type-details.keys.js';
import ProjectEventTypeDetailsForm from './components/ProjectEventTypeDetailsForm.vue';
import ThSortable from '@/app/components/ThSortable';
import TolButton from '@/app/components/TolButton';
import TolTable from '@/app/components/TolTable';
import UpdateModal from '@/app/components/UpdateModal';

export default {
  name: 'ProjectEventTypeDetails',
  components: {
    AddBillingModal,
    AddSpecialRequirementModal,
    AddTechRatesModal,
    AddToolModal,
    Attachment,
    AttachmentsList,
    Checkbox,
    ConfirmModal,
    DropdownSelect,
    InputField,
    ProjectEventTypeDetailsForm,
    ThSortable,
    TolButton,
    TolTable,
    UpdateModal
  },
  mixins: [dateUtils],
  data() {
    return {
      updatedEventType: {},
      isOnScreenDataModified: false,
      isListDisplayed: {
        ATTACHMENTS: false,
        TECH_RATES: false,
        BILLING: false,
        TOOLS: false,
        SPECIAL_REQUIREMENTS: false
      },
      isModalDisplayed: {
        TECH_RATES: false,
        BILLING: false,
        TOOLS: false,
        SPECIAL_REQUIREMENTS: false
      },
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null,
      attachmentOptions: [
        'Install (PS)',
        'Floorplan (PS)',
        'Sign Off (PS)',
        'Authorization Form (PS)',
        'Other (PS)',
        'Information Update (PS)'
      ],
      techRatesColumns: [
        'rate_code',
        'pay_staff',
        'pay_sole',
        'pay_inc',
        'bill_rate',
        'start_date',
        'end_date',
        'comments'
      ],
      labourRatesTypes: ['ACTUAL', 'FIXED_PER_TECH', 'FIXED_PER_EVENT', 'FIXED_PER_ASSET'],
      travelRatesTypes: ['ACTUAL', 'FIXED_PER_TECH', 'FIXED_PER_EVENT'],
      toolsColumns: ['name', 'comments'],
      specialRequirementsColumns: ['description', 'value', 'comments']
    };
  },
  computed: {
    canUserSave() {
      if (this.currentUser) {
        return this.currentUser.permissions['PROJECT']['SAVE'];
      }
      return false;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isLoading() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.IsProjectEventTypeDetailsLoading];
    },
    isSaving() {
      return this.$store.getters.IsSavingProjectEventType;
    },
    iconFor() {
      return toggle => (toggle ? 'eye' : 'eye-slash');
    },
    toggleAttachments() {
      return this.isListDisplayed['ATTACHMENTS']
        ? this.$t('projects.eventTypes.details.labels.hideAttachments')
        : this.$t('projects.eventTypes.details.labels.showAttachments');
    },
    toggleTechRates() {
      return this.isListDisplayed['TECH_RATES']
        ? this.$t('projects.eventTypes.details.labels.hideTechRates')
        : this.$t('projects.eventTypes.details.labels.showTechRates');
    },
    toggleBilling() {
      return this.isListDisplayed['BILLING']
        ? this.$t('projects.eventTypes.details.labels.hideBilling')
        : this.$t('projects.eventTypes.details.labels.showBilling');
    },
    toggleTools() {
      return this.isListDisplayed['TOOLS']
        ? this.$t('projects.eventTypes.details.labels.hideTools')
        : this.$t('projects.eventTypes.details.labels.showTools');
    },
    toggleSpecialRequirements() {
      return this.isListDisplayed['SPECIAL_REQUIREMENTS']
        ? this.$t('projects.eventTypes.details.labels.hideSpecialRequirements')
        : this.$t('projects.eventTypes.details.labels.showSpecialRequirements');
    },
    isTesEmployee() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).some(role => role === 'TES Employee')
      );
    },
    formErrors() {
      return this.$store.getters.ProjectEventTypeValidationErrors;
    },
    statusClass() {
      let className = '';
      switch (this.updatedEventType.status) {
        case 'ACTIVE':
          className = 'active';
          break;
        case 'INACTIVE':
          className = 'inactive';
          break;
        case 'RECONCILED':
          className = 'reconciled';
          break;
        case 'COMPLETED':
          className = 'completed';
          break;
      }
      return className;
    },
    eventType: {
      get() {
        return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventType];
      }
    },
    eventTypeAttachments() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventTypeAttachments];
    },
    allTools: {
      get() {
        return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectTools];
      }
    },
    currentSortFieldForTechRates() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventTypeTechRatesSortOrder]
        .sortBy;
    },
    currentSortDirectionForTechRates() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventTypeTechRatesSortOrder]
        .sortDirection;
    },
    currentSortFieldForTools() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventTypeToolsSortOrder]
        .sortBy;
    },
    currentSortDirectionForTools() {
      return this.$store.getters[ProjectEventTypeDetailsGetters.ProjectEventTypeToolsSortOrder]
        .sortDirection;
    },
    currentSortFieldForSpecialRequirements() {
      return this.$store.getters[
        ProjectEventTypeDetailsGetters.ProjectEventTypeSpecialRequirementsSortOrder
      ].sortBy;
    },
    currentSortDirectionForSpecialRequirements() {
      return this.$store.getters[
        ProjectEventTypeDetailsGetters.ProjectEventTypeSpecialRequirementsSortOrder
      ].sortDirection;
    },
    canViewAttachments() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions['PROJECT']['VIEW_ATTACHMENT']
      );
    },
    objectHeader() {
      const data = {
        referenceNumber: this.updatedEventType.reference_number,
        objectStatus: this.updatedEventType.status,
        objectType: 'PROJECT_EVENT_TYPE',
        lastModified: null
      };
      const lastModified = `${formatDateTime(this.updatedEventType.updated_dt)}`;
      if (this.isTesEmployee) {
        data.lastModified = lastModified;
      }
      return data;
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler(val) {
        this.$store.dispatch(
          ProjectEventTypeDetailsActions.FetchProjectEventType,
          val.params.reference_number
        );
        if (this.isListDisplayed['TOOLS']) {
          this.$store.dispatch(
            ProjectEventTypeDetailsActions.FetchProjectTools,
            val.params.reference_number
          );
        }
        if (this.isListDisplayed['ATTACHMENTS']) {
          this.$store.dispatch(
            ProjectEventTypeDetailsActions.FetchProjectEventTypeAttachments,
            this.$route.params.reference_number
          );
        }
      }
    },
    eventType: {
      immediate: true,
      handler() {
        this.updatedEventType = clone(this.eventType);

        // Create up to 3 empty client support contacts
        if (
          this.updatedEventType.client_support_contacts &&
          this.updatedEventType.client_support_contacts.length < 3
        ) {
          const numberOfContactsToAdd = 3 - this.updatedEventType.client_support_contacts.length;
          const emptyClientSupportContact = {
            type: null,
            phone_number: '',
            email: ''
          };
          for (let i = 0; i < numberOfContactsToAdd; i++) {
            this.updatedEventType.client_support_contacts.push(emptyClientSupportContact);
          }
        }
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('projects.eventTypes.details.title'));
    this.$store.dispatch(
      ProjectEventTypeDetailsActions.FetchProjectEventType,
      this.$route.params.reference_number
    );
  },
  methods: {
    toggleList(item) {
      this.isListDisplayed[item] = !this.isListDisplayed[item];
      if (item === 'ATTACHMENTS' && this.isListDisplayed[item]) {
        this.$store.dispatch(
          ProjectEventTypeDetailsActions.FetchProjectEventTypeAttachments,
          this.$route.params.reference_number
        );
      }
      if (item === 'TOOLS' && this.isListDisplayed[item]) {
        this.$store.dispatch(
          ProjectEventTypeDetailsActions.FetchProjectTools,
          this.$route.params.reference_number
        );
      }
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    onTechRatesSortOrderChange(sortOrder) {
      this.$store.dispatch(
        ProjectEventTypeDetailsActions.SetProjectEventTypeTechRatesSortOrder,
        sortOrder
      );
    },
    onToolsSortOrderChange(sortOrder) {
      this.$store.dispatch(
        ProjectEventTypeDetailsActions.SetProjectEventTypeToolsSortOrder,
        sortOrder
      );
    },
    onSpecialRequirementsSortOrderChange(sortOrder) {
      this.$store.dispatch(
        ProjectEventTypeDetailsActions.SetProjectEventTypeSpecialRequirementsSortOrder,
        sortOrder
      );
    },
    refreshAttachments() {
      this.$store.dispatch(
        ProjectEventTypeDetailsActions.FetchProjectEventTypeAttachments,
        this.$route.params.reference_number
      );
    },
    displayAddModal(type) {
      this.isModalDisplayed[type] = true;
    },
    addReferencedObject(event) {
      if (event.type === 'BILLING') {
        this.updatedEventType.labour_rates = event.value.labour_rates;
        this.updatedEventType.travel_rates = event.value.travel_rates;
      } else {
        if (this.updatedEventType[event.field]) {
          this.updatedEventType[event.field].push(event.value);
        } else {
          this.updatedEventType[event.field] = [event.value];
        }
      }

      this.isModalDisplayed[event.type] = false;
      this.isOnScreenDataModified = true;
    },
    updateOnScreenValue(fieldName, event = null) {
      if (fieldName === 'client_support_contacts') {
        let selectedContact;
        if (event.id) {
          selectedContact = this.updatedEventType.client_support_contacts.find(
            contact => contact.id === event.id
          );
        } else {
          selectedContact = this.updatedEventType.client_support_contacts[event.index];
        }
        selectedContact[event.field] = event.value;
      } else {
        this.updatedEventType[fieldName] = event;
      }

      this.isOnScreenDataModified = true;
    },
    saveEventTypeUpdates() {
      if (!this.isOnScreenDataModified && this.eventType.reference_number) {
        return;
      }

      const updates = {};
      Object.keys(this.updatedEventType).forEach(key => {
        if (!this.eventType[key] && !this.updatedEventType[key]) {
          return;
        }

        if (Array.isArray(this.updatedEventType[key])) {
          // Filter out empty client contacts that have not been updated
          if (key === 'client_support_contacts') {
            const nonEmptyContacts = [];

            this.updatedEventType[key].forEach(contact => {
              let isEmpty = true;
              Object.values(contact).forEach(field => {
                if (field) {
                  isEmpty = false;
                }
              });
              if (!isEmpty) {
                nonEmptyContacts.push(contact);
              }
            });

            this.updatedEventType[key] = nonEmptyContacts;
          }

          let hasBeenUpdated = false;

          // Check if any item was added or removed in the property
          // of the updated event type compared to the previous version
          this.eventType[key].forEach(item => {
            const existsInUpdatedEventType = this.updatedEventType[key].find(itemInUpdated => {
              return itemInUpdated.id === item.id;
            });
            if (!existsInUpdatedEventType) {
              hasBeenUpdated = true;
            }
          });

          if (!hasBeenUpdated) {
            this.updatedEventType[key].forEach(item => {
              const existsInEventType = this.eventType[key].find(itemInUpdated => {
                return itemInUpdated.id === item.id;
              });
              if (!existsInEventType) {
                hasBeenUpdated = true;
              }
            });
          }

          // If any item in the array was added or removed, add the property to updates
          if (hasBeenUpdated) {
            updates[key] = this.updatedEventType[key];
          }
        } else if (typeof this.updatedEventType[key] === 'object') {
          // Check if the updated event type field is not an object with all null property values
          let isEmpty = true;
          for (let objectValue of Object.values(this.updatedEventType[key])) {
            if (objectValue !== null) {
              isEmpty = false;
              break;
            }
          }

          if (!isEmpty) {
            updates[key] = {};

            if (!this.eventType[key]) {
              Object.keys(this.updatedEventType[key]).forEach(objectKey => {
                if (this.updatedEventType[key][objectKey] !== null) {
                  updates[key][objectKey] = this.updatedEventType[key][objectKey];
                }
              });
            } else {
              Object.keys(this.updatedEventType[key]).forEach(objectKey => {
                if (
                  this.updatedEventType[key][objectKey] !== this.eventType[key][objectKey] &&
                  this.updatedEventType[key][objectKey] !== null
                ) {
                  updates[key][objectKey] = this.updatedEventType[key][objectKey];
                }
              });
            }

            // If there is nothing to update, remove the property from updates object to avoid sending it empty
            if (Object.keys(updates[key]).length === 0) {
              delete updates[key];
            }
          }
        } else {
          if (this.updatedEventType[key] !== this.eventType[key]) {
            updates[key] = this.updatedEventType[key];
          }
        }
      });

      this.$store.dispatch(ProjectEventTypeDetailsActions.SaveProjectEventType, {
        updates,
        referenceNumber: this.eventType.reference_number
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.details-wrapper {
  display: flex;
  flex-direction: column;
  background-color: $background-level-2;

  .details-body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      padding: 0 1.5rem;

      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px 0;
      }
    }

    .details-section {
      margin-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }

      .details-list-container {
        display: flex;
        justify-content: space-between;
      }

      .details-list {
        margin-top: 1rem;
      }
    }

    .event-type-status {
      position: fixed;
      width: 170px;
      padding: 10px 0;
      transform: rotate(-90deg);
      top: 50%;
      right: 0;
      color: $white;
      font-weight: bold;
      text-align: center;
      &.active {
        background-color: $secondary-green;
        border: 1px solid $green;
      }
      &.inactive {
        background-color: $purple;
        border: 1px solid $purple;
      }
      &.completed {
        background-color: $blue;
        border: 1px solid $dark-blue;
      }
    }
  }

  .billing-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;

    .billing-dropdown-container {
      flex: 1;
      margin-right: 7.5rem;
    }

    .billing-values {
      display: flex;
      align-items: center;
      flex: 2;

      .billing-values-column {
        margin-right: 5rem;
      }
    }
  }

  .module-action-container {
    justify-content: flex-end;
  }

  .add-button {
    display: flex;
    justify-content: flex-end;
    span {
      display: inline-block;
      font-size: 1.2em;
      font-weight: bold;
      width: 20px;
      height: 20px;
      line-height: 20px;
      border-radius: 100%;
      text-align: center;
      &:hover {
        cursor: pointer;
        background-color: $light-grey-2;
      }
    }
  }

  .empty {
    display: flex;
    padding: 60px;
  }
}
</style>
